<template>
  <div class="icd-page">
    <div class="page-header">
      <PageHeaderPortal>
<div class="page-header-left">
        <div class="page-icon">
          <i class="fa-duotone fa-file-signature"></i>
        </div>
        <div>
          <h1 class="page-title">Initial CD Processing</h1>
          <p class="page-subtitle">Phase 1 disclosure review queue and compliance status</p>
        </div>
      </div>
      </PageHeaderPortal>

      <div class="header-actions">
        <select v-model="timeFrame" class="filter-select days-select" @change="loadData">
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="this_week">This Week</option>
          <option value="last_week">Last Week</option>
          <option value="last_7_days">Last 7 Days</option>
          <option value="last_30_days">Last 30 Days</option>
          <option value="last_90_days">Last 90 Days</option>
        </select>

        <button class="refresh-btn" @click="loadData" :disabled="loading">
          {{ loading ? 'Loading...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <!-- Queue Status Bar -->
    <div class="queue-status-bar" v-if="queueStatus">
      <div class="queue-status-label">
        <i class="fa-duotone fa-database"></i>
        Live Queue
      </div>
      <div class="queue-pill pending">
        <span class="q-count">{{ queueStatus.pending }}</span>
        <span class="q-label">Pending</span>
      </div>
      <div class="queue-pill processing">
        <span class="q-count">{{ queueStatus.processing }}</span>
        <span class="q-label">Processing</span>
      </div>
      <div class="queue-pill done">
        <span class="q-count">{{ queueStatus.done }}</span>
        <span class="q-label">Done</span>
      </div>
      <div class="queue-pill errored">
        <span class="q-count">{{ queueStatus.error }}</span>
        <span class="q-label">Error</span>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="summary-sections">
      <div class="summary-section">
        <div class="section-header">
          <div class="section-title-wrap">
            <i class="fa-duotone fa-list-check section-icon"></i>
            <span class="section-title">Queue Overview</span>
          </div>
        </div>
        <div class="section-cards">
          <div class="summary-card">
            <div class="summary-card-header">
              <i class="fa-duotone fa-table-rows summary-icon"></i>
              <span class="summary-label">Total Processed</span>
            </div>
            <div class="summary-value">{{ summary.total }}</div>
          </div>
          <div class="summary-card">
            <div class="summary-card-header">
              <i class="fa-duotone fa-flask summary-icon"></i>
              <span class="summary-label">Dry Runs</span>
            </div>
            <div class="summary-value">{{ summary.dryRuns }}</div>
          </div>
        </div>
      </div>

      <div class="summary-section decisions-section">
        <div class="section-header">
          <div class="section-title-wrap">
            <i class="fa-duotone fa-sparkles section-icon"></i>
            <span class="section-title">Decisions</span>
          </div>
        </div>
        <div class="section-cards">
          <div class="summary-card approve">
            <div class="summary-card-header">
              <i class="fa-duotone fa-circle-check summary-icon"></i>
              <span class="summary-label">Passed</span>
            </div>
            <div class="summary-value">{{ summary.passed }}</div>
          </div>
          <div class="summary-card manual-review">
            <div class="summary-card-header">
              <i class="fa-duotone fa-circle-exclamation summary-icon"></i>
              <span class="summary-label">Kicked Back</span>
            </div>
            <div class="summary-value">{{ summary.kickedBack }}</div>
          </div>
          <div class="summary-card request-only">
            <div class="summary-card-header">
              <i class="fa-duotone fa-eye summary-icon"></i>
              <span class="summary-label">Manual Review</span>
            </div>
            <div class="summary-value">{{ summary.manualReview }}</div>
          </div>
          <div class="summary-card no-audit">
            <div class="summary-card-header">
              <i class="fa-duotone fa-triangle-exclamation summary-icon"></i>
              <span class="summary-label">Errors</span>
            </div>
            <div class="summary-value">{{ summary.errors }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="toolbar single-line-toolbar">
      <input
        v-model.trim="search"
        type="text"
        class="search-input"
        placeholder="Search loan #, borrower, decision, kickback reason, summary..."
      />
      <select v-model="decisionFilter" class="filter-select toolbar-select">
        <option value="">All Decisions</option>
        <option value="pass">Passed</option>
        <option value="kicked_back">Kicked Back</option>
        <option value="manual_review">Manual Review</option>
        <option value="skipped">Skipped</option>
        <option value="error">Error</option>
      </select>
      <select v-model="dryRunFilter" class="filter-select toolbar-select">
        <option value="">Live + Dry Runs</option>
        <option value="live">Live Only</option>
        <option value="dry">Dry Runs Only</option>
      </select>
    </div>

    <!-- Table -->
    <div class="table-card">
      <div class="table-card-header">
        <div class="table-card-title-wrap">
          <i class="fa-duotone fa-clipboard-list-check table-card-icon"></i>
          <span class="table-card-title">Initial Disclosure Log</span>
        </div>
        <div class="table-card-count">{{ filteredRows.length }}</div>
      </div>

      <div v-if="loading" class="table-state">Loading initial disclosure log...</div>
      <div v-else-if="errorMessage" class="table-state error">{{ errorMessage }}</div>
      <div v-else-if="pagedRows.length === 0" class="table-state">No records found.</div>

      <div v-else class="table-wrap">
        <table class="locks-table">
          <thead>
            <tr>
              <th @click="setSort('processedatutc')">
                Processed <span>{{ sortIcon('processedatutc') }}</span>
              </th>
              <th @click="setSort('loannumber')">
                Loan # <span>{{ sortIcon('loannumber') }}</span>
              </th>
              <th @click="setSort('borrowername')">
                Borrower <span>{{ sortIcon('borrowername') }}</span>
              </th>
              <th @click="setSort('decision')">
                Decision <span>{{ sortIcon('decision') }}</span>
              </th>
              <th>Checks</th>
              <th @click="setSort('lockvalidationvalue')">
                Lock Val <span>{{ sortIcon('lockvalidationvalue') }}</span>
              </th>
              <th @click="setSort('firstpaymentdate')">
                1st Payment <span>{{ sortIcon('firstpaymentdate') }}</span>
              </th>
              <th>Dry Run</th>
              <th>Details</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="row in pagedRows" :key="row.id">
              <td class="compact-cell">{{ formatDateTime(row.processedatutc) }}</td>

              <td>
                <div class="primary-text">{{ row.loannumber || '—' }}</div>
      
              </td>

              <td>{{ row.borrowername || '—' }}</td>

              <td>
                <span class="decision-pill" :class="decisionClass(row.decision)">
                  {{ decisionLabel(row.decision) }}
                </span>
              </td>

              <!-- Check badges -->
              <td>
                <div class="check-badges">
                  <span class="check-badge" :class="badgeClass(row.allitemssigned)" title="All Items Signed">Sig</span>
                  <span class="check-badge" :class="badgeClass(row.wetsigsverified)" title="Wet Signatures">Wet</span>
                  <span class="check-badge" :class="badgeClass(row.datesinorder)" title="Dates In Order">Dt</span>
                  <span class="check-badge" :class="badgeClass(row.cancelledverified)" title="Cancelled Verified">Cxl</span>
                  <span class="check-badge" :class="badgeClass(row.lockvalidationpassed)" title="Lock Validation">Lck</span>
                  <span class="check-badge" :class="badgeClass(row.paymentdatecorrect)" title="Payment Date">Pmt</span>
                </div>
              </td>

              <td class="compact-cell">
                <span :class="row.lockvalidationpassed ? 'val-ok' : 'val-fail'">
                  {{ row.lockvalidationvalue || '—' }}
                </span>
              </td>

              <td class="compact-cell">{{ row.firstpaymentdate ? formatDate(row.firstpaymentdate) : '—' }}</td>

              <td class="compact-cell">
                <span class="status-pill" :class="row.dryrun ? 'pending' : 'processed'">
                  {{ row.dryrun ? 'Dry' : 'Live' }}
                </span>
              </td>

              <td>
                <button
                  class="summary-btn"
                  :disabled="!row.summary && !row.kickbackreasons && !row.error"
                  @click="openDetailModal(row)"
                >
                  Details
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="filteredRows.length" class="pagination-bar">
      <div class="pagination-info">
        Showing {{ pageStart }} to {{ pageEnd }} of {{ filteredRows.length }}
      </div>
      <div class="pagination-actions">
        <button class="page-btn" @click="prevPage" :disabled="page === 1">Prev</button>
        <span class="page-number">Page {{ page }} of {{ totalPages }}</span>
        <button class="page-btn" @click="nextPage" :disabled="page === totalPages">Next</button>
      </div>
    </div>

    <!-- Detail Modal -->
    <div v-if="showDetailModal" class="modal-backdrop" @click.self="closeDetailModal">
      <div class="modal-card">
        <div class="modal-header">
          <h3 class="modal-title">Disclosure Review Details</h3>
          <button class="close-btn" @click="closeDetailModal">✕</button>
        </div>

        <div class="modal-body">
          <!-- Meta -->
          <div class="reason-meta">
            <div><strong>Loan #:</strong> {{ selectedRow.loannumber || selectedRow.loanid || '—' }}</div>
            <div><strong>Borrower:</strong> {{ selectedRow.borrowername || '—' }}</div>
            <div><strong>Decision:</strong> {{ decisionLabel(selectedRow.decision) }}</div>
            <div><strong>Processed:</strong> {{ formatDateTime(selectedRow.processedatutc) }}</div>
            <div><strong>Run Type:</strong> {{ selectedRow.dryrun ? 'Dry Run' : 'Live' }}</div>
          </div>

          <!-- Check grid -->
          <div class="check-grid">
            <div class="check-row" v-for="check in checksFor(selectedRow)" :key="check.label">
              <i class="fa-duotone check-icon"
                 :class="check.value === true ? 'fa-circle-check check-pass'
                       : check.value === false ? 'fa-circle-xmark check-fail'
                       : 'fa-circle-minus check-na'"></i>
              <span class="check-label">{{ check.label }}</span>
              <span v-if="check.extra" class="check-extra">{{ check.extra }}</span>
            </div>
          </div>

          <!-- Kickback reasons -->
          <div v-if="kickbackList(selectedRow).length" class="section-block">
            <div class="block-title"><i class="fa-duotone fa-triangle-exclamation"></i> Kickback Reasons</div>
            <ul class="kickback-list">
              <li v-for="(reason, i) in kickbackList(selectedRow)" :key="i">{{ reason }}</li>
            </ul>
          </div>

          <!-- Summary -->
          <div v-if="selectedRow.summary" class="section-block">
            <div class="block-title"><i class="fa-duotone fa-memo"></i> Summary</div>
            <div class="reason-content">{{ selectedRow.summary }}</div>
          </div>

          <!-- Error -->
          <div v-if="selectedRow.error" class="section-block">
            <div class="block-title" style="color:#b24553"><i class="fa-duotone fa-bug"></i> Error</div>
            <div class="reason-content error-content">{{ selectedRow.error }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeaderPortal from "@/views/PageHeaderPortal.vue"
import api from '@/api/api.service'
import auth from '@/authConfig.js'

export default {
  components: { PageHeaderPortal },
  name: 'InitialDisclosureDashboard',
  data() {
    return {
      loading: false,
      errorMessage: '',
      rows: [],
      queueStatus: null,
      timeFrame: 'last_7_days',
      search: '',
      decisionFilter: '',
      dryRunFilter: '',
      sortBy: 'processedatutc',
      sortDir: 'desc',
      page: 1,
      pageSize: 100,
      showDetailModal: false,
      selectedRow: {}
    }
  },
  computed: {
    visibleRows() {
      return (this.rows || []).filter(x => this.matchesTimeFrame(x?.processedatutc))
    },
    summary() {
      const rows = this.visibleRows
      return {
        total:        rows.length,
        dryRuns:      rows.filter(x => !!x.dryrun).length,
        passed:       rows.filter(x => x.decision === 'pass').length,
        kickedBack:   rows.filter(x => x.decision === 'kicked_back').length,
        manualReview: rows.filter(x => x.decision === 'manual_review').length,
        errors:       rows.filter(x => x.decision === 'error' || !!x.error).length
      }
    },
    filteredRows() {
      let rows = [...this.visibleRows]

      if (this.search) {
        const term = this.search.toLowerCase()
        rows = rows.filter(x => {
          const haystack = [
            x.loanid,
            x.loannumber,
            x.borrowername,
            x.decision,
            x.kickbackreasons,
            x.summary,
            x.lockvalidationvalue,
            x.firstpaymentdate,
            x.error
          ].filter(Boolean).join(' ').toLowerCase()
          return haystack.includes(term)
        })
      }

      if (this.decisionFilter) {
        rows = rows.filter(x => x.decision === this.decisionFilter)
      }

      if (this.dryRunFilter === 'dry') {
        rows = rows.filter(x => !!x.dryrun)
      } else if (this.dryRunFilter === 'live') {
        rows = rows.filter(x => !x.dryrun)
      }

      rows.sort((a, b) => {
        const aVal = this.sortValue(a, this.sortBy)
        const bVal = this.sortValue(b, this.sortBy)
        if (aVal === bVal) return 0
        if (aVal == null) return 1
        if (bVal == null) return -1
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return this.sortDir === 'asc' ? aVal - bVal : bVal - aVal
        }
        const r = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' })
        return this.sortDir === 'asc' ? r : -r
      })

      return rows
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize))
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize
      return this.filteredRows.slice(start, start + this.pageSize)
    },
    pageStart() {
      if (!this.filteredRows.length) return 0
      return (this.page - 1) * this.pageSize + 1
    },
    pageEnd() {
      return Math.min(this.page * this.pageSize, this.filteredRows.length)
    }
  },
  watch: {
    search()         { this.page = 1 },
    decisionFilter() { this.page = 1 },
    dryRunFilter()   { this.page = 1 },
    timeFrame()      { this.page = 1 },
    filteredRows()   { if (this.page > this.totalPages) this.page = 1 }
  },
  async mounted() {
    await this.loadData()
  },
  methods: {
    getRequestDays() {
      if (this.timeFrame === 'today')        return 1
      if (this.timeFrame === 'yesterday')    return 2
      if (this.timeFrame === 'this_week')    return 7
      if (this.timeFrame === 'last_week')    return 14
      if (this.timeFrame === 'last_7_days')  return 7
      if (this.timeFrame === 'last_30_days') return 30
      if (this.timeFrame === 'last_90_days') return 90
      return 7
    },
    async loadData() {
      this.loading = true
      this.errorMessage = ''
      try {
        const tokenResponse = await auth.acquireTokenResponse()
        const token = tokenResponse.accessToken

        const [logRes, queueRes] = await Promise.all([
          api.GetInitialDisclosureLog(this.getRequestDays(), token),
          api.GetInitialDisclosureQueueStatus(token)
        ])

        this.rows        = logRes?.data?.data  || []
        this.queueStatus = queueRes?.data?.data || null
      } catch (e) {
        this.rows = []
        this.errorMessage =
          e?.response?.data?.message ||
          e?.message ||
          'Failed to load initial disclosure log.'
      } finally {
        this.loading = false
      }
    },

    // ── Decision helpers ────────────────────────────────────────────────────
    decisionLabel(decision) {
      if (decision === 'pass')         return 'Passed'
      if (decision === 'kicked_back')  return 'Kicked Back'
      if (decision === 'manual_review') return 'Manual Review'
      if (decision === 'skipped')      return 'Skipped'
      if (decision === 'error')        return 'Error'
      return decision || '—'
    },
    decisionClass(decision) {
      if (decision === 'pass')          return 'approve'
      if (decision === 'kicked_back')   return 'manual-review'
      if (decision === 'manual_review') return 'request-only'
      if (decision === 'skipped')       return 'no-audit'
      if (decision === 'error')         return 'error-pill'
      return 'no-audit'
    },

    // ── Check badge helpers ─────────────────────────────────────────────────
    badgeClass(val) {
      if (val === true)  return 'badge-pass'
      if (val === false) return 'badge-fail'
      return 'badge-na'
    },
    checksFor(row) {
      return [
        { label: 'All Items Signed',    value: row.allitemssigned },
        { label: 'Wet Signatures',      value: row.wetsigsverified },
        { label: 'Dates In Order',      value: row.datesinorder },
        { label: 'Cancelled Verified',  value: row.cancelledverified },
        { label: 'Lock Validation',     value: row.lockvalidationpassed, extra: row.lockvalidationvalue },
        { label: 'Payment Date Correct',value: row.paymentdatecorrect,   extra: row.firstpaymentdate ? this.formatDate(row.firstpaymentdate) : null }
      ]
    },
    kickbackList(row) {
      if (!row.kickbackreasons) return []
      return row.kickbackreasons.split('|').map(s => s.trim()).filter(Boolean)
    },

    // ── Formatting ──────────────────────────────────────────────────────────
    formatDateTime(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      })
    },
    formatDate(value) {
      if (!value) return '—'
      const d = new Date(value)
      if (isNaN(d)) return value
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    truncateId(id) {
      if (!id) return ''
      return id.length > 12 ? id.substring(0, 8) + '…' : id
    },

    // ── Sorting ─────────────────────────────────────────────────────────────
    sortValue(row, key) {
      if (key === 'processedatutc') return new Date(row.processedatutc).getTime()
      if (key === 'dryrun')         return row.dryrun ? 1 : 0
      return row[key]
    },
    setSort(key) {
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
        return
      }
      this.sortBy  = key
      this.sortDir = 'asc'
    },
    sortIcon(key) {
      if (this.sortBy !== key) return ''
      return this.sortDir === 'asc' ? '▲' : '▼'
    },

    // ── Time frame ──────────────────────────────────────────────────────────
    startOfDay(date)  { const d = new Date(date); d.setHours(0,0,0,0); return d },
    endOfDay(date)    { const d = new Date(date); d.setHours(23,59,59,999); return d },
    startOfWeek(date) {
      const d = this.startOfDay(date)
      const day = d.getDay()
      d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day))
      return d
    },
    endOfWeek(date) {
      const d = this.startOfWeek(date)
      d.setDate(d.getDate() + 6)
      d.setHours(23,59,59,999)
      return d
    },
    matchesTimeFrame(value) {
      if (!value) return false
      const rowDate = new Date(value)
      if (isNaN(rowDate)) return false
      const now = new Date()
      const todayStart = this.startOfDay(now)
      const todayEnd   = this.endOfDay(now)
      if (this.timeFrame === 'today')     return rowDate >= todayStart && rowDate <= todayEnd
      if (this.timeFrame === 'yesterday') {
        const y = new Date(todayStart); y.setDate(y.getDate() - 1)
        return rowDate >= this.startOfDay(y) && rowDate <= this.endOfDay(y)
      }
      if (this.timeFrame === 'this_week') return rowDate >= this.startOfWeek(now) && rowDate <= this.endOfWeek(now)
      if (this.timeFrame === 'last_week') {
        const twS = this.startOfWeek(now)
        const lwS = new Date(twS); lwS.setDate(lwS.getDate() - 7)
        const lwE = new Date(twS); lwE.setMilliseconds(-1)
        return rowDate >= lwS && rowDate <= lwE
      }
      if (this.timeFrame === 'last_7_days')  { const s = this.startOfDay(now); s.setDate(s.getDate()-6);  return rowDate >= s && rowDate <= todayEnd }
      if (this.timeFrame === 'last_30_days') { const s = this.startOfDay(now); s.setDate(s.getDate()-29); return rowDate >= s && rowDate <= todayEnd }
      if (this.timeFrame === 'last_90_days') { const s = this.startOfDay(now); s.setDate(s.getDate()-89); return rowDate >= s && rowDate <= todayEnd }
      return true
    },

    // ── Pagination ──────────────────────────────────────────────────────────
    prevPage() { if (this.page > 1) this.page-- },
    nextPage() { if (this.page < this.totalPages) this.page++ },

    // ── Modal ───────────────────────────────────────────────────────────────
    openDetailModal(row)  { this.selectedRow = row || {}; this.showDetailModal = true },
    closeDetailModal()    { this.showDetailModal = false; this.selectedRow = {} }
  }
}
</script>

<style scoped>
.icd-page {
  padding: 18px;
  background: #f7f8fb;
  min-height: calc(100vh - 90px);
  display: flex;
  flex-direction: column;
  color: #1f2a44;
}

/* ── Header ─────────────────────────────────────────────── */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}
.page-header-left { display: flex; align-items: center; gap: 14px; }
.page-icon {
  width: 48px; height: 48px;
  border-radius: 5px;
  background: #123a71;
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px;
  flex: 0 0 auto;
}
.page-title  { margin: 0; font-size: 20px; font-weight: 700; color: #1f2a44; letter-spacing: -.01em; }
.page-subtitle { margin: 4px 0 0; font-size: 13px; color: #7b8397; font-weight: 500; }
.header-actions { display: flex; gap: 8px; align-items: center; }

/* ── Queue status bar ───────────────────────────────────── */
.queue-status-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f0f4fb;
  border: 1px solid #dde4f0;
  border-radius: 5px;
  padding: 8px 14px;
  margin-bottom: 12px;
  font-size: 13px;
}
.queue-status-label { color: #5f79c7; font-weight: 700; display: flex; align-items: center; gap: 6px; margin-right: 4px; }
.queue-pill {
  display: flex; align-items: center; gap: 5px;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid transparent;
}
.queue-pill.pending    { background: #f2f4f7; border-color: #e1e5ec; color: #6d7789; }
.queue-pill.processing { background: #edf4ff; border-color: #d7e5fd; color: #3d6fd1; }
.queue-pill.done       { background: #eef9f0; border-color: #d9eedf; color: #2c7a4b; }
.queue-pill.errored    { background: #fff0f1; border-color: #f0d1d5; color: #b24553; }
.q-count { font-size: 15px; font-weight: 800; }
.q-label { font-size: 11px; font-weight: 600; opacity: .75; }

/* ── Summary cards ──────────────────────────────────────── */
.summary-sections {
  display: grid;
  grid-template-columns: 0.7fr 1.6fr;
  gap: 12px;
  margin-bottom: 12px;
}
.summary-section {
  background: #fbfbfd;
  border: 1px solid #e7ebf2;
  border-radius: 5px;
  padding: 12px;
}
.decisions-section { background: #fcfbff; }
.section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.section-title-wrap { display: flex; align-items: center; gap: 8px; }
.section-icon  { color: #5f79c7; font-size: 14px; }
.section-title { font-size: 14px; font-weight: 700; color: #28344f; }
.section-cards { display: grid; gap: 10px; }
.summary-section:first-child .section-cards { grid-template-columns: 1fr 1fr; }
.decisions-section .section-cards { grid-template-columns: repeat(4, 1fr); }
.summary-card {
  background: #fdfdfe;
  border: 1px solid #e8ebf2;
  border-radius: 5px;
  padding: 12px;
}
.summary-card.approve       { border-color: #d8eedf; background: #fbfefc; }
.summary-card.manual-review { border-color: #f2d7d9; background: #fffafb; }
.summary-card.request-only  { border-color: #f4e7c4; background: #fffdfa; }
.summary-card.no-audit      { border-color: #e8ebf2; background: #fbfbfd; }
.summary-card-header { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.summary-icon  { font-size: 13px; color: #6a7b9f; }
.summary-label { font-size: 12px; color: #7b8397; font-weight: 600; }
.summary-value { font-size: 22px; font-weight: 800; color: #1f2a44; line-height: 1.1; }

/* ── Toolbar ────────────────────────────────────────────── */
.toolbar { margin-bottom: 10px; }
.single-line-toolbar {
  display: grid;
  grid-template-columns: 1fr 180px 180px;
  gap: 8px;
  align-items: center;
}
.search-input,
.filter-select {
  height: 38px;
  border: 1px solid #dfe4ee;
  background: #fcfcfe;
  border-radius: 5px;
  padding: 0 14px;
  font-size: 13px;
  width: 100%;
  color: #23314f;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.search-input::placeholder { color: #94a0b8; }
.search-input:focus, .filter-select:focus {
  border-color: #cfd8e8;
  box-shadow: 0 0 0 2px rgba(49,93,181,.08);
}
.days-select { min-width: 140px; }

/* ── Buttons ────────────────────────────────────────────── */
.refresh-btn, .page-btn, .close-btn, .summary-btn {
  border: 1px solid #d9dfeb;
  border-radius: 5px;
  background: #fff;
  color: #22314f;
  height: 38px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s, border-color .15s;
}
.refresh-btn:hover, .page-btn:hover, .summary-btn:hover { background: #f4f7fc; border-color: #cfd8e8; }
.summary-btn { height: 30px; padding: 0 12px; font-size: 12px; color: #315db5; background: #f5f8fc; border-color: #d9e2f0; }
.refresh-btn:disabled, .page-btn:disabled, .summary-btn:disabled { opacity: .55; cursor: not-allowed; }

/* ── Table ──────────────────────────────────────────────── */
.table-card {
  background: #fbfbfd;
  border: 1px solid #e7ebf2;
  border-radius: 5px;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.table-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #e9edf3;
  background: #fafbfd;
}
.table-card-title-wrap { display: flex; align-items: center; gap: 8px; }
.table-card-icon  { color: #5f79c7; font-size: 14px; }
.table-card-title { font-size: 14px; font-weight: 700; color: #28344f; }
.table-card-count { font-size: 13px; font-weight: 700; color: #7b8397; }
.table-wrap { overflow: auto; flex: 1; }
.locks-table { width: 100%; border-collapse: separate; border-spacing: 0; min-width: 1100px; }
.locks-table th, .locks-table td {
  padding: 11px 12px;
  border-bottom: 1px solid #edf0f5;
  font-size: 13px;
  text-align: left;
  vertical-align: middle;
}
.locks-table th {
  background: #f7f9fc;
  color: #6f7b92;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  position: sticky;
  top: 0;
  z-index: 1;
}
.locks-table tbody tr { background: #fbfbfd; }
.locks-table tbody tr:hover { background: #f8faff; }
.primary-text { font-weight: 700; color: #24314f; }
.loan-id-text { font-family: monospace; font-size: 11px; color: #8a96ae; }
.compact-cell { white-space: nowrap; }

/* ── Decision pills ─────────────────────────────────────── */
.decision-pill {
  display: inline-flex; align-items: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px; font-weight: 700;
  white-space: nowrap;
  border: 1px solid transparent;
}
.decision-pill.approve       { background: #eef9f0; border-color: #d9eedf; color: #2c7a4b; }
.decision-pill.manual-review { background: #fff0f1; border-color: #f0d1d5; color: #b24553; }
.decision-pill.request-only  { background: #fff7e7; border-color: #f5e3b5; color: #9a6b11; }
.decision-pill.no-audit      { background: #f2f4f7; border-color: #e1e5ec; color: #6d7789; }
.decision-pill.error-pill    { background: #fff0f1; border-color: #f0d1d5; color: #b24553; }

/* ── Status pills ───────────────────────────────────────── */
.status-pill {
  display: inline-flex; align-items: center;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 11px; font-weight: 700;
  border: 1px solid transparent;
}
.status-pill.processed { background: #edf4ff; border-color: #d7e5fd; color: #3d6fd1; }
.status-pill.pending   { background: #f2f4f7; border-color: #e1e5ec; color: #6d7789; }

/* ── Check badges ───────────────────────────────────────── */
.check-badges { display: flex; gap: 3px; flex-wrap: wrap; }
.check-badge {
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 3px;
  padding: 2px 5px;
  font-size: 10px; font-weight: 700;
  border: 1px solid transparent;
}
.check-badge.badge-pass { background: #eef9f0; border-color: #d9eedf; color: #2c7a4b; }
.check-badge.badge-fail { background: #fff0f1; border-color: #f0d1d5; color: #b24553; }
.check-badge.badge-na   { background: #f2f4f7; border-color: #e1e5ec; color: #9da8bc; }

.val-ok   { color: #2c7a4b; font-weight: 700; }
.val-fail { color: #b24553; font-weight: 700; }

/* ── Pagination ─────────────────────────────────────────── */
.pagination-bar { display: flex; justify-content: space-between; align-items: center; padding: 10px 4px 0; gap: 12px; }
.pagination-info    { color: #7b8397; font-size: 13px; font-weight: 500; }
.pagination-actions { display: flex; align-items: center; gap: 8px; }
.page-number        { font-size: 13px; color: #5c6780; font-weight: 600; }

.table-state       { padding: 28px; text-align: center; color: #7b8397; font-size: 14px; }
.table-state.error { color: #b94b5c; }

/* ── Modal ──────────────────────────────────────────────── */
.modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(28,37,56,.32);
  display: flex; align-items: center; justify-content: center;
  padding: 24px; z-index: 1000;
}
.modal-card {
  width: min(740px, 100%);
  max-height: 85vh;
  background: #fcfcfe;
  border: 1px solid #e7ebf2;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 18px 44px rgba(28,37,56,.12);
  display: flex; flex-direction: column;
}
.modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid #e9edf3;
  background: #fafbfd;
  flex: 0 0 auto;
}
.modal-title { margin: 0; font-size: 16px; color: #22314f; font-weight: 700; }
.close-btn   { width: 36px; height: 36px; padding: 0; }
.modal-body  { padding: 18px; overflow-y: auto; flex: 1; }

.reason-meta {
  display: grid; gap: 5px;
  margin-bottom: 16px;
  color: #5f6b84; font-size: 13px;
}

/* ── Check grid in modal ────────────────────────────────── */
.check-grid { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
.check-row  { display: flex; align-items: center; gap: 8px; font-size: 13px; }
.check-icon { font-size: 15px; flex: 0 0 auto; }
.check-pass { color: #2c7a4b; }
.check-fail { color: #b24553; }
.check-na   { color: #9da8bc; }
.check-label { color: #28344f; font-weight: 600; }
.check-extra { color: #6a7b9f; font-size: 12px; margin-left: 4px; }

/* ── Section blocks in modal ────────────────────────────── */
.section-block { margin-bottom: 14px; }
.block-title {
  font-size: 12px; font-weight: 700; color: #5f79c7;
  margin-bottom: 6px; display: flex; align-items: center; gap: 6px;
  text-transform: uppercase; letter-spacing: .04em;
}
.kickback-list {
  margin: 0; padding: 0 0 0 16px;
  font-size: 13px; line-height: 1.7; color: #b24553;
}
.reason-content {
  font-size: 13px; line-height: 1.65; color: #24314f;
  white-space: pre-wrap; word-break: break-word;
  background: #f7f9fc; border: 1px solid #e6ebf2;
  border-radius: 5px; padding: 12px;
}
.error-content { border-color: #f0d1d5; background: #fff0f1; color: #b24553; }

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 1400px) { .summary-sections { grid-template-columns: 1fr; } }
@media (max-width: 1100px) { .decisions-section .section-cards { grid-template-columns: 1fr 1fr; } }
@media (max-width: 900px)  {
  .page-header { flex-direction: column; align-items: stretch; }
  .single-line-toolbar { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 700px)  {
  .icd-page { padding: 16px; }
  .single-line-toolbar { grid-template-columns: 1fr; }
  .summary-section:first-child .section-cards { grid-template-columns: 1fr; }
  .decisions-section .section-cards { grid-template-columns: 1fr 1fr; }
  .pagination-bar { flex-direction: column; align-items: flex-start; }
  .queue-status-bar { flex-wrap: wrap; }
}
</style>
